import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const GenerationContext = createContext();

export const useGeneration = () => {
  const context = useContext(GenerationContext);
  if (!context) {
    throw new Error('useGeneration must be used within a GenerationProvider');
  }
  return context;
};

export const GenerationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [generationCount, setGenerationCount] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [lastGeneratedTool, setLastGeneratedTool] = useState(null);

  // Load user's generation count from Firestore
  useEffect(() => {
    const loadGenerationCount = async () => {
      if (currentUser) {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          setGenerationCount(userDoc.data().generationCount || 0);
          setLastGeneratedTool(userDoc.data().lastGeneratedTool || null);
        } else {
          // Initialize user document if it doesn't exist
          await setDoc(userRef, {
            generationCount: 0,
            lastGeneratedTool: null
          });
        }
      }
    };

    loadGenerationCount();
  }, [currentUser]);

  const checkGenerationLimit = async (toolName) => {
    if (!currentUser) {
      setShowUpgradeModal(true);
      return false;
    }

    if (generationCount >= 1) {
      setShowUpgradeModal(true);
      return false;
    }

    return true;
  };

  const incrementGeneration = async (toolName) => {
    if (currentUser) {
      const newCount = generationCount + 1;
      const userRef = doc(db, 'users', currentUser.uid);
      
      await setDoc(userRef, {
        generationCount: newCount,
        lastGeneratedTool: toolName
      }, { merge: true });

      setGenerationCount(newCount);
      setLastGeneratedTool(toolName);
    }
  };

  const resetGenerationCount = async () => {
    if (currentUser) {
      const userRef = doc(db, 'users', currentUser.uid);
      await setDoc(userRef, {
        generationCount: 0,
        lastGeneratedTool: null
      }, { merge: true });

      setGenerationCount(0);
      setLastGeneratedTool(null);
    }
  };

  return (
    <GenerationContext.Provider
      value={{
        generationCount,
        lastGeneratedTool,
        showUpgradeModal,
        setShowUpgradeModal,
        checkGenerationLimit,
        incrementGeneration,
        resetGenerationCount
      }}
    >
      {children}
    </GenerationContext.Provider>
  );
};
